import React, { FC, useState } from 'react';
import { Skeleton } from 'antd';
import useInterval from '../../hooks/useInterval';

// Rotating copy while the model works. Ordered to mirror how each job actually
// proceeds, so the wait reads as progress rather than a spinner.
export const COMPLIANCE_EVALUATION_LOADING_MESSAGES = [
	'Reading your document…',
	"Matching it against your client's requirements…",
	'Checking coverage limits…',
	'Checking policy dates…',
	'Checking named parties and addresses…',
	'Looking for required signatures…',
	'Preparing your results…',
];

export const COMPLIANCE_EXTRACTION_LOADING_MESSAGES = [
	'Reading your sample document…',
	'Looking for annotations and markup…',
	'Pulling out coverage limits…',
	'Reading policy dates and required forms…',
	'Noting named parties and addresses…',
	'Turning them into checkable requirements…',
	'Preparing your checklist…',
];

const COPY_ROTATION_MS = 2500;

interface ComplianceAiLoadingSkeletonProps {
	messages: string[];
}

// Shaped like the sectioned list that replaces it — a summary line, then section
// blocks — so the panel does not jump when the real content arrives. Shared by the
// supplier's document check and the buyer's sample extraction, which both end in
// that shape.
export const ComplianceAiLoadingSkeleton: FC<ComplianceAiLoadingSkeletonProps> = ({
	messages,
}): React.ReactElement => {
	const [copyIndex, setCopyIndex] = useState(0);
	useInterval(() => setCopyIndex((previous) => (previous + 1) % messages.length), COPY_ROTATION_MS);

	const renderSectionBlock = (key: number) => (
		<div key={key} className="flex flex-col gap-2">
			<Skeleton.Input active={true} style={{ width: 180, height: 22 }} size="small" />
			<Skeleton active={true} title={false} paragraph={{ rows: 3 }} />
		</div>
	);

	return (
		<div className="flex flex-col gap-6">
			<div className="h4medium text-character-primary">{messages[copyIndex]}</div>
			<Skeleton.Input active={true} style={{ width: '100%', height: 56 }} />
			<div className="flex flex-row gap-6">
				<Skeleton.Input active={true} style={{ width: 90 }} size="small" />
				<Skeleton.Input active={true} style={{ width: 90 }} size="small" />
				<Skeleton.Input active={true} style={{ width: 120 }} size="small" />
			</div>
			{[0, 1, 2].map(renderSectionBlock)}
		</div>
	);
};
